import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatINR, formatDistance, resolveImage, formatSize } from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, MessageCircle, Train, TrendingUp, MapPin, Home, Crown } from "lucide-react";
import { LOGO_URL, BRAND_NAME, BRAND_TAGLINE, SUPPORT_EMAIL, buildShortlistShareText, whatsappLink } from "../lib/brand";
// v2.80 — Public shortlist pages must refresh when admin edits any
// property they contain (e.g. price drop, sold flag). Subscribes
// without authentication; relies on the same `data.sync` WS broadcast.
import { useDataSync } from "../lib/dataSync";

export default function ShortlistPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get(`/shortlists/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // v2.80 — Refresh if any property in this shortlist mutates.
  useDataSync(["properties", "shortlists"], (_domains, payload) => {
    if (!data) return;
    if (payload?.id) {
      const ids = new Set((data.properties || []).map((p) => p.id));
      if (!ids.has(payload.id)) return;
    }
    load();
  });

  const print = () => window.print();

  const shareWhatsApp = () => {
    const link = window.location.href;
    const text = buildShortlistShareText(data.shortlist, data.properties.length, link);
    window.open(whatsappLink(text), "_blank", "noopener,noreferrer");
  };

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#A1A1AA]">
        Shortlist not found.
      </div>
    );
  }
  if (!data) return <div className="max-w-[1100px] mx-auto p-10 text-[#A1A1AA]">Loading…</div>;

  const { shortlist, properties } = data;

  return (
    <div className="min-h-screen bg-white shortlist-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .shortlist-page { background: white !important; }
          .prop-card { page-break-inside: avoid; box-shadow: none !important; border: 1px solid #E4E4E7 !important; }
          .shortlist-grid { grid-template-columns: 1fr 1fr !important; gap: 16px !important; }
          .shortlist-prop-img { height: 160px !important; }
          @page { margin: 16mm 12mm; }
        }
      `}</style>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap pb-6 border-b-2 border-[#09090B] mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#09090B] rounded-sm flex items-center justify-center overflow-hidden">
              <img src={LOGO_URL} alt={BRAND_NAME} className="w-full h-full object-contain" loading="lazy" decoding="async" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0055FF]">
                ◉ Curated Shortlist
              </div>
              <div className="font-heading font-bold text-[15px] tracking-tight">
                House<span className="text-[#E53935]">Entry</span> <span className="text-[#52525B]">Solutions</span>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold">{BRAND_TAGLINE}</div>
            </div>
          </div>
          <div className="flex gap-2 no-print">
            <Button onClick={shareWhatsApp} variant="outline" className="rounded-sm border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white" data-testid="shortlist-whatsapp">
              <MessageCircle size={14} className="mr-1.5" /> WhatsApp
            </Button>
            <Button onClick={print} className="btn-brand rounded-sm" data-testid="shortlist-print">
              <Printer size={14} className="mr-1.5" /> Print / Save PDF
            </Button>
          </div>
        </div>

        {/* Title */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090B]" data-testid="shortlist-title">
            {shortlist.name}
          </h1>
          {shortlist.client_name && (
            <div className="text-[14px] text-[#52525B] mt-1">
              Curated for <b className="text-[#09090B]">{shortlist.client_name}</b>
            </div>
          )}
          <div className="text-[12px] text-[#A1A1AA] mt-2 font-mono-data">
            {properties.length} properties · {new Date(shortlist.created_at).toLocaleDateString()}
          </div>
          {shortlist.notes && (
            <div className="mt-4 p-4 bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm text-[13px] text-[#52525B] italic">
              "{shortlist.notes}"
            </div>
          )}
        </div>

        {/* Properties grid */}
        {properties.length === 0 ? (
          <div className="text-center py-20 text-[#A1A1AA] border border-dashed border-[#E4E4E7] rounded-sm">
            No properties in this shortlist.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 shortlist-grid">
            {properties.map((p, i) => {
            const img = p.images?.[0] ? resolveImage(p.images[0], { w: 600 }) : null;
              return (
                <div key={p.id} className="prop-card bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid={`shortlist-prop-${p.id}`}>
                  <div className="relative aspect-[4/3] bg-[#F4F4F5] shortlist-prop-img">
                    {img ? <img src={img} alt={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" /> :
                      <div className="w-full h-full flex items-center justify-center text-[#A1A1AA]"><Home size={48} strokeWidth={1} /></div>}
                    <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap max-w-[80%]">
                      {(p.tags || []).slice(0, 2).map((t) => (
                        <span key={t} className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${
                          t === "Near Metro" ? "tag-metro" :
                          t === "Best Deal" ? "tag-best" :
                          t === "Priority" ? "bg-[#0055FF] text-white border border-[#0055FF]" : "tag-roi"
                        }`}>
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="absolute bottom-3 right-3 bg-[#09090B] text-white px-2.5 py-1 rounded-sm">
                      <div className="text-[9px] uppercase tracking-wider text-white/60 font-semibold leading-none">ROI</div>
                      <div className="text-[13px] font-bold font-mono-data leading-tight">{p.roi?.toFixed(1) ?? "—"}%</div>
                    </div>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#A1A1AA] font-semibold flex items-center gap-1.5">
                      <MapPin size={11} /> {p.area} · {p.bhk} · {p.property_type}
                    </div>
                    <h3 className="font-heading font-semibold text-[14px] tracking-tight text-[#09090B] leading-snug">
                      {p.title}
                    </h3>
                    <div className="flex items-end justify-between pt-2 border-t border-[#F4F4F5]">
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-[#A1A1AA] font-semibold">Price</div>
                        <div className="font-heading font-bold text-lg text-[#0055FF] font-mono-data">{formatINR(p.price)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[9px] uppercase tracking-wider text-[#A1A1AA] font-semibold flex items-center gap-1 justify-end"><Train size={10} /> Metro</div>
                        <div className="text-[12px] font-semibold text-[#09090B] font-mono-data">{formatDistance(p.distance_metro_m)}</div>
                      </div>
                    </div>
                    <div className="text-[11px] text-[#52525B] flex items-center gap-3 pt-1">
                      <span className="font-mono-data text-[#09090B]"><b>{formatSize(p.size_sqft, p.size_unit)}</b></span>
                      {p.floor && <span>· {p.floor}</span>}
                      {p.monthly_rent > 0 && (
                        <span className="ml-auto text-[#15803D] font-semibold inline-flex items-center gap-1">
                          <TrendingUp size={11} /> ₹{p.monthly_rent.toLocaleString("en-IN")}/mo
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-[#E4E4E7] text-center">
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#52525B] flex items-center justify-center gap-2">
            <Crown size={12} className="text-[#0055FF]" /> {BRAND_NAME}
          </div>
          <div className="text-[12px] text-[#A1A1AA] mt-2">
            {BRAND_TAGLINE} · <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#0055FF] hover:underline">{SUPPORT_EMAIL}</a>
          </div>
          <div className="no-print mt-3">
            <Link to="/" className="text-[12px] text-[#0055FF] hover:underline">Browse all listings →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
